// ============================================================================
// CommitteeSummaryHeader — header sommaire de la fiche comite (E3a, F-409)
// ============================================================================
// FRERE de CompanySummaryHeader / PersonSummaryHeader (apps/ug — gabarit nav
// fiche UG, standard permanent MASTER-E3A) : tuile + titre + chips statut /
// date cible + bande de metriques gap-px (jalons done/total, membres,
// derniere MAJ). Pattern documente dans docs/standards/UI_STANDARDS.md.

import { UsersRound } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { CommitteeStatusBadge } from './ComitesPage';
import { formatDate, formatDateOnly } from './dates';
import type { CommitteeStatus } from '../../hooks/useCommittees';

export interface CommitteeSummaryHeaderProps {
  name: string;
  description: string | null;
  status: CommitteeStatus;
  targetDate: string | null;
  createdAt: string;
  milestonesDone: number;
  milestonesTotal: number;
  memberCount: number;
  lastUpdateAt: string | null;
}

export function CommitteeSummaryHeader({
  name, description, status, targetDate, createdAt,
  milestonesDone, milestonesTotal, memberCount, lastUpdateAt,
}: CommitteeSummaryHeaderProps) {
  const { t, i18n } = useTranslation();

  const metrics = [
    {
      label: t('gest:comites.metrics.milestones'),
      value: milestonesTotal > 0 ? `${milestonesDone} / ${milestonesTotal}` : '—',
    },
    { label: t('gest:comites.metrics.members'), value: String(memberCount) },
    {
      label: t('gest:comites.metrics.lastUpdate'),
      value: lastUpdateAt ? formatDate(i18n.language, lastUpdateAt) : '—',
    },
  ];

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400">
          <UsersRound size={20} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-head font-bold text-slate-900 dark:text-slate-50">{name}</h2>
            <CommitteeStatusBadge status={status} />
            {targetDate ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-chip font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {t('gest:comites.header.target', { date: formatDateOnly(i18n.language, targetDate) })}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">
            {t('gest:comites.header.createdAt', { date: formatDate(i18n.language, createdAt) })}
          </p>
          {description ? (
            <p className="mt-1.5 whitespace-pre-wrap text-field text-slate-600 dark:text-slate-300">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-px border-t border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-700">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white px-3.5 py-2.5 dark:bg-slate-800">
            <div className="text-chip font-semibold uppercase tracking-wide text-slate-400">{m.label}</div>
            <div className="mt-0.5 truncate text-field font-semibold tabular-nums text-slate-800 dark:text-slate-100">
              {m.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
